import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { RowClickedEvent, FirstDataRenderedEvent, GetRowIdParams, IRowNode } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IAppBuild } from '~/types/build'

import columns from './columns'
import ModalUpdate from './ModalUpdate'
import PanelContent from './PanelContent'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import Panel from '~/components/Panel'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useBuildsListQuery } from '~/queries/builds'
import { isFocusedCellActions } from '~/utils/table'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const Builds = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const { binaryFileId } = useParams<{ binaryFileId?: string }>()

  const gridRef = useRef<AgGridReact<IAppBuild>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [panelRef, setPanelRef] = useState<HTMLDivElement>()

  const [selectedBuild, setSelectedBuild] = useState<IAppBuild>()

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  /** Queries & Mutations */

  const { data: builds } = useBuildsListQuery()

  /** Effects */

  useDocumentTitle('Builds')

  const handleClosePanel = useCallback(() => {
    gridRef.current?.api?.deselectAll()
    setSelectedBuild(undefined)
    navigate('/builds', { replace: true })
  }, [navigate])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (panelRef &&
          tableRef.current &&
          (tableRef.current.contains(event.target as Node) || panelRef.contains(event.target as Node))) ||
        !document.getElementById('root')?.contains(event.target as Node)
      ) {
        return
      }

      handleClosePanel()
    }

    gridRef.current?.api?.sizeColumnsToFit()
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [gridRef, handleClosePanel, panelRef, tableRef])

  /** Handlers */

  const contentRefCallback = (node: HTMLDivElement) => setPanelRef(node)

  // Callback for Panel auto opening when version ID in URL
  const handleFirstDataRendered = useCallback(
    (evt: FirstDataRenderedEvent<IAppBuild>) => {
      if (binaryFileId) {
        // NOTE: do not use destructuring on the node object otherwise ag-grid will lose
        // the this reference because of the arrow function
        evt.api.forEachNode((node: IRowNode<IAppBuild>) => {
          if (node.data && node.data.binary_file_id === binaryFileId) {
            setSelectedBuild(node.data)

            if (node.rowIndex) {
              evt.api.ensureIndexVisible(node.rowIndex, 'middle')
            }

            node.setSelected(true)
          }
        })
      }
    },
    [binaryFileId]
  )

  const handleRowClicked = (evt: RowClickedEvent<IAppBuild>) => {
    if (gridRef.current?.api && !isFocusedCellActions(evt)) {
      setSelectedBuild(evt.data)

      if (gridRef.current.api.getFocusedCell()?.column.getColId() !== 'actions' && evt.data) {
        navigate(`/builds/${evt.data.binary_file_id}`, { replace: true })
      }
    }
  }

  const handleUpdateModalState = (isOpen: boolean, build?: IAppBuild) => () => {
    setSelectedBuild(build)
    setIsUpdateModalOpen(isOpen)
  }

  const handleSuccess = (newBuild: IAppBuild) => {
    gridRef.current?.api.applyTransaction({ update: [newBuild] })
    setIsUpdateModalOpen(false)
    displayToast('Build successfully updated', 'success')
  }

  const getRowId = useCallback(({ data }: GetRowIdParams<IAppBuild>) => data.binary_file_id, [])

  /** Render */

  return (
    <>
      <Layout className="builds-view" withPadding>
        <ViewTitle className="layout-view-title">Builds</ViewTitle>
        <ContentLayout loading={false}>
          <Section>
            <TableWrapper
              className="builds-table with-min-height"
              columnDefs={columns}
              context={{ handleEdit: handleUpdateModalState }}
              entriesName="builds"
              getRowId={getRowId}
              onFirstDataRendered={handleFirstDataRendered}
              onRowClicked={handleRowClicked}
              ref={gridRef}
              rowData={builds}
              tableRef={tableRef}
              withPagination={false}
              withSearchBar
            />
          </Section>
        </ContentLayout>
      </Layout>
      <Panel
        className="build-panel"
        isOpen={!!selectedBuild}
        onClosePanel={handleClosePanel}
        panelRef={contentRefCallback}
      >
        {selectedBuild ? <PanelContent build={selectedBuild} /> : null}
      </Panel>
      {selectedBuild ? (
        <ModalUpdate
          build={selectedBuild}
          isOpen={isUpdateModalOpen}
          onClose={handleUpdateModalState(false)}
          onSuccess={handleSuccess}
        />
      ) : null}
    </>
  )
}

export default Builds
